import { HttpErrorResponse } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { Component, inject, input, OnInit, output } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TooltipModule } from 'primeng/tooltip';
import {
  FieldTreeItem,
  LayoutDetailResponse,
  LayoutFieldItem,
  SaveLayoutRequest,
} from '../../../models/api/query/query-layouts';
import { Snackbar } from '../../../services/snackbar/snackbar.service';
import { BaseComponent } from '../../base/base.component';
import { ButtonComponent } from '../../core/buttons/button.component';
import { ExpandButtonComponent } from '../../core/buttons/expand-button.component';
import { CheckboxComponent } from '../../core/controls/checkbox.component';
import { InputComponent } from '../../core/controls/input.component';
import { SelectComponent } from '../../core/controls/select.component';
import { SearchFilterStatusComponent } from '../../core/labels/search-filter-status.component';
import { ActionRowComponent } from '../../core/panels/action-row.component';
import {
  firstStringControlError,
  layoutDescriptionConstraintsValidator,
  layoutDescriptionMaxLength,
  layoutFormatValidator,
  layoutNameConstraintsValidator,
  layoutNameMaxLength,
} from './layout-editor.validators';

interface TreeNode extends FieldTreeItem {
  uid: string;
  expanded: boolean;
  loading: boolean;
  children: TreeNode[];
  checked: boolean;
}

@Component({
  selector: 'app-layout-editor',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonComponent,
    CheckboxComponent,
    InputComponent,
    SelectComponent,
    ActionRowComponent,
    ExpandButtonComponent,
    TooltipModule,
    SearchFilterStatusComponent,
  ],
  template: `
    <div>
      <h2 class="tw-text-lg tw-font-semibold tw-mb-4">
        {{
          isViewOnly
            ? 'View Layout'
            : hasLayoutId
              ? 'Edit Layout'
              : hasCopyLayoutId
                ? 'Copy Layout'
                : 'Create Custom Layout'
        }}
      </h2>

      <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4 tw-mb-6">
        <app-input
          [control]="form.controls.name"
          label="Layout Name"
          [maxlength]="layoutNameMaxLength"
        />
        <app-input
          [control]="form.controls.description"
          label="Layout Description"
          [maxlength]="layoutDescriptionMaxLength"
        />
        <div class="tw-flex tw-items-end tw-pb-2 md:tw-col-span-2">
          <app-checkbox [control]="form.controls.isPrivate" label="Private" />
        </div>
        <app-select
          [control]="form.controls.format"
          label="Output Format"
          [options]="formatOptions"
          optionLabel="label"
          optionValue="value"
        />
      </div>

      <div
        class="tw-gap-4 tw-items-start tw-mb-6"
        [ngClass]="
          isViewOnly
            ? 'tw-grid tw-grid-cols-1 tw-max-w-full md:tw-max-w-md'
            : 'tw-grid tw-grid-cols-1 md:tw-grid-cols-[1fr_auto_1fr]'
        "
      >
        @if (!isViewOnly) {
          <div>
            <app-select
              [control]="searchModeControl"
              label="Search By"
              [options]="searchModeOptions"
              optionLabel="label"
              optionValue="value"
              [filter]="false"
              [showClear]="false"
              class="tw-w-[200px]"
            />

            @if (searchModeControl.value === 'Category') {
              <div class="tw-font-bold tw-pb-2">Fields Available</div>
              @if (loading) {
                <div class="tw-text-[var(--sed-black-60-color)]">
                  Loading fields...
                </div>
              } @else {
                <div
                  class="tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-h-[300px] tw-overflow-auto tw-bg-[var(--sed-warm-white-color)]"
                >
                  <ng-container
                    *ngTemplateOutlet="
                      treeTemplate;
                      context: { nodes: treeNodes, depth: 0 }
                    "
                  ></ng-container>
                </div>

                <ng-template #treeTemplate let-nodes="nodes" let-depth="depth">
                  @for (node of nodes; track node.uid) {
                    @if (!isFieldSelected(node.fieldId)) {
                      <div
                        class="tw-select-none"
                        [style.padding-left.px]="depth * 16"
                      >
                        @if (node.hasChildren) {
                          <div
                            class="tw-flex tw-items-center tw-cursor-pointer hover:tw-bg-[var(--sed-grey-40-color)] tw-py-0.5"
                            (click)="toggleNode(node)"
                          >
                            <app-expand-button
                              [expanded]="node.expanded"
                              [type]="'Right'"
                              [disableClick]="true"
                            />
                            <span class="tw-font-semibold tw-text-sm">{{
                              node.fieldName
                            }}</span>
                            @if (node.loading) {
                              <span
                                class="tw-text-xs tw-text-[var(--sed-black-60-color)] tw-ml-2"
                              >
                                Loading...
                              </span>
                            }
                          </div>
                          @if (node.expanded && node.children.length > 0) {
                            <ng-container
                              *ngTemplateOutlet="
                                treeTemplate;
                                context: {
                                  nodes: node.children,
                                  depth: depth + 1,
                                }
                              "
                            ></ng-container>
                          }
                        } @else {
                          <!-- Native checkbox: virtual tree list; app-checkbox would require per-node FormControls. -->
                          <div
                            class="tw-flex tw-items-center tw-py-0.5 hover:tw-bg-[var(--sed-grey-40-color)]"
                          >
                            <input
                              type="checkbox"
                              [id]="'field-' + node.fieldId"
                              [checked]="node.checked"
                              (change)="onNodeCheckChange(node, $event)"
                              class="tw-mr-2 tw-cursor-pointer"
                            />
                            <label
                              [for]="'field-' + node.fieldId"
                              class="tw-cursor-pointer tw-text-sm"
                              [pTooltip]="node.description"
                              tooltipPosition="right"
                              [tooltipDisabled]="!node.description"
                            >
                              {{ node.fieldName }}
                            </label>
                          </div>
                        }
                      </div>
                    }
                  }
                </ng-template>
              }
            } @else {
              <div class="tw-font-bold tw-pb-2">Field Name Contains</div>
              <app-search-filter-status
                announceMode="filter"
                [count]="availableSearchResultCount"
                [query]="searchControl.value"
                [enabled]="!searchLoading && hasSearched"
                statusStyle="matching"
                recordLabel="field"
                recordLabelPlural="fields"
              />
              <div class="tw-flex tw-flex-col sm:tw-flex-row tw-gap-2 tw-mb-2">
                <app-input
                  [control]="searchControl"
                  placeholder="Enter field name..."
                  class="tw-flex-1 tw-min-w-0"
                  inputMode="search"
                  [showHelpText]="false"
                />
                <app-button label="Search" (click)="searchByName()" />
              </div>
              <div class="tw-font-bold tw-pb-2">Search Results</div>
              @if (searchLoading) {
                <div class="tw-text-[var(--sed-black-60-color)]">
                  Searching...
                </div>
              } @else {
                <div
                  class="tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2 tw-h-[250px] tw-overflow-auto tw-bg-[var(--sed-warm-white-color)]"
                >
                  @for (field of searchResults; track $index) {
                    @if (!isFieldSelected(field.fieldId)) {
                      <div
                        class="tw-py-1 tw-px-2 tw-cursor-pointer hover:tw-bg-[var(--sed-grey-100-color)]"
                        [class.tw-bg-[var(--sed-grey-100-color)]]="
                          isSearchResultSelected(field)
                        "
                        (click)="toggleSearchResultSelection(field, $event)"
                        [pTooltip]="field.description"
                        tooltipPosition="right"
                        [tooltipDisabled]="!field.description"
                      >
                        {{ field.fieldName }}
                      </div>
                    }
                  }
                  @if (
                    availableSearchResultCount === 0 &&
                    hasSearched &&
                    searchControl.value
                  ) {
                    <div
                      class="tw-text-[var(--sed-black-60-color)] tw-py-2"
                      aria-hidden="true"
                    >
                      No fields found matching '{{ searchControl.value }}'
                    </div>
                  }
                </div>
              }
              <div
                class="tw-text-xs tw-text-[var(--sed-black-60-color)] tw-mt-1"
              >
                * Results limited to 1000 fields. Please use specific field
                names when possible.
              </div>
            }
          </div>
        }

        @if (!isViewOnly) {
          <div class="tw-flex tw-flex-col tw-gap-2 tw-pt-10">
            <app-button
              label="→"
              ariaLabel="Add selected fields"
              (click)="addCheckedFields()"
            />
            <app-button
              label="←"
              ariaLabel="Remove selected fields"
              (click)="removeSelectedFields(selectedFieldsSelect)"
            />
          </div>
        }

        <div>
          <label
            for="layout-editor-selected-fields"
            class="tw-font-bold tw-pb-2 tw-block"
          >
            Selected Fields
          </label>
          <!-- Native multi-select: HTML listbox semantics for drag-free reorder UX with Move Up/Down. -->
          <select
            id="layout-editor-selected-fields"
            #selectedFieldsSelect
            multiple
            size="12"
            class="tw-w-full tw-h-[300px] tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2"
            (change)="onSelectedFieldsSelectionChange(selectedFieldsSelect)"
          >
            @for (field of selectedFields; track field.fieldId) {
              <option [value]="field.fieldId">
                {{ field.fieldName }}
              </option>
            }
          </select>
          @if (!isViewOnly && showSelectedFieldsError) {
            <div
              class="tw-text-[var(--sed-status-red-color)] tw-text-sm tw-font-bold tw-pt-1"
              role="alert"
            >
              You must select at least one field before saving
            </div>
          }
          @if (!isViewOnly) {
            <div class="tw-flex tw-gap-2 tw-pt-2">
              <app-button
                label="Move Up"
                emphasis="Low"
                (click)="moveSelectedFieldUp(selectedFieldsSelect)"
              />
              <app-button
                label="Move Down"
                emphasis="Low"
                (click)="moveSelectedFieldDown(selectedFieldsSelect)"
              />
            </div>
          }
        </div>
      </div>

      @if (error) {
        <div
          class="tw-bg-[color-mix(in_srgb,var(--sed-status-red-color)_12%,white)] tw-border tw-border-[var(--sed-status-red-color)] tw-rounded tw-p-3 tw-mt-4 tw-text-[var(--sed-engaged-black-color)]"
          role="alert"
        >
          {{ error }}
        </div>
      }
      @if (success) {
        <div
          class="tw-bg-[color-mix(in_srgb,var(--sed-status-green-color)_12%,white)] tw-border tw-border-[var(--sed-status-green-color)] tw-rounded tw-p-3 tw-mt-4 tw-text-[var(--sed-engaged-black-color)]"
          role="status"
        >
          {{ success }}
        </div>
      }

      <app-action-row class="tw-pt-8">
        <app-button
          left1
          [label]="isViewOnly ? 'Back' : 'Cancel'"
          emphasis="Low"
          (click)="cancel()"
        />
        @if (!isViewOnly) {
          <app-button
            right1
            [label]="
              hasLayoutId
                ? 'Update Layout'
                : hasCopyLayoutId
                  ? 'Save as New Layout'
                  : 'Save Layout'
            "
            [loading]="saving"
            (click)="save()"
          />
        }
      </app-action-row>
    </div>
  `,
  styles: ``,
})
export class LayoutEditorComponent extends BaseComponent implements OnInit {
  private readonly snackbar = inject(Snackbar);

  layoutId = input<number | null>(null);
  copyLayoutId = input<number | null>(null);
  viewOnly = input<boolean>(false);
  cancelled = output<void>();
  saved = output<number>();

  form = new FormGroup({
    name: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, layoutNameConstraintsValidator()],
    }),
    description: new FormControl('', {
      nonNullable: true,
      validators: [
        Validators.required,
        layoutDescriptionConstraintsValidator(),
      ],
    }),
    format: new FormControl('CSV', {
      nonNullable: true,
      validators: [Validators.required, layoutFormatValidator()],
    }),
    /** Legacy LayoutDetail._private defaults to true (CreateLayoutSave Private checkbox). */
    isPrivate: new FormControl<boolean>(true, { nonNullable: true }),
  });

  readonly layoutNameMaxLength = layoutNameMaxLength;
  readonly layoutDescriptionMaxLength = layoutDescriptionMaxLength;

  searchControl = new FormControl('');
  searchModeControl = new FormControl<'Category' | 'Name'>('Category', {
    nonNullable: true,
  });

  formatOptions = [
    { label: 'Comma Separated (CSV)', value: 'CSV' },
    { label: 'Tabbed ASCII (TAB)', value: 'TAB' },
    { label: 'Fixed ASCII (TXT)', value: 'TXT' },
    { label: 'HTML/Web Page (HTM)', value: 'HTM' },
  ];

  private readonly supportedFormats = ['CSV', 'TAB', 'TXT', 'HTM'] as const;

  private normalizeFormat(format: string | null | undefined): string {
    const upper = format?.trim().toUpperCase();
    return upper &&
      this.supportedFormats.includes(
        upper as (typeof this.supportedFormats)[number],
      )
      ? upper
      : 'CSV';
  }

  searchModeOptions = [
    { label: 'Category', value: 'Category' },
    { label: 'Name', value: 'Name' },
  ];

  treeNodes: TreeNode[] = [];
  selectedFields: LayoutFieldItem[] = [];
  searchResults: LayoutFieldItem[] = [];
  selectedSearchResults = new Set<number>();
  hasSearched = false;
  loading = false;
  searchLoading = false;
  saving = false;
  error = '';
  success = '';
  showSelectedFieldsError = false;

  /** Mirrors native listbox selection; survives option re-render after reorder. */
  private selectedFieldFocusIds: number[] = [];

  isViewOnly = false;
  hasLayoutId = false;
  hasCopyLayoutId = false;
  layoutIdValue: number | null = null;
  copyLayoutIdValue: number | null = null;

  async ngOnInit(): Promise<void> {
    this.isViewOnly = this.viewOnly();
    this.hasLayoutId = !!this.layoutId();
    this.hasCopyLayoutId = !!this.copyLayoutId();
    this.layoutIdValue = this.layoutId();
    this.copyLayoutIdValue = this.copyLayoutId();

    this.dispose.subs = this.searchControl.valueChanges.subscribe(() => {
      this.hasSearched = false;
    });

    this.dispose.subs = this.searchModeControl.valueChanges.subscribe(() => {
      this.uncheckAllNodes(this.treeNodes);
      this.selectedSearchResults.clear();
      this.searchResults = [];
      this.hasSearched = false;
    });

    await this.loadFieldCategories();

    if (this.hasLayoutId) {
      await this.loadLayout();
    } else if (this.hasCopyLayoutId) {
      await this.loadLayoutForCopy();
    }

    if (this.isViewOnly) {
      this.form.disable();
    }
  }

  async loadFieldCategories(): Promise<void> {
    this.loading = true;
    try {
      const response = await this.api.query.getFieldCategories();
      this.treeNodes = response.items.map((item) => this.toTreeNode(item, 0));
    } finally {
      this.loading = false;
    }
  }

  private toTreeNode(item: FieldTreeItem, parentId: number): TreeNode {
    return {
      ...item,
      uid: `${item.fieldId}-${item.fieldLink}-${parentId}`,
      expanded: false,
      loading: false,
      children: [],
      checked: false,
    };
  }

  async toggleNode(node: TreeNode): Promise<void> {
    if (node.expanded) {
      node.expanded = false;
      return;
    }

    if (node.children.length === 0 && node.hasChildren) {
      node.loading = true;
      try {
        const response = await this.api.query.getFieldCategories(node.fieldId);
        node.children = response.items.map((item) =>
          this.toTreeNode(item, node.fieldId),
        );
      } finally {
        node.loading = false;
      }
    }

    node.expanded = true;
  }

  isFieldSelected(fieldId: number): boolean {
    return this.selectedFields.some((f) => f.fieldId === fieldId);
  }

  onNodeCheckChange(node: TreeNode, event: Event): void {
    node.checked = (event.target as HTMLInputElement).checked;
  }

  async loadLayout(): Promise<void> {
    const id = this.layoutIdValue;
    if (!id) {
      return;
    }

    try {
      const response: LayoutDetailResponse = await this.api.query.getLayout(id);
      this.form.patchValue({
        name: response.name,
        description: response.description ?? '',
        format: this.normalizeFormat(response.format),
        isPrivate: !response.isPublic,
      });
      this.selectedFields = [...response.selectedFields];
    } catch {
      this.error = 'Failed to load layout details.';
    }
  }

  async loadLayoutForCopy(): Promise<void> {
    const id = this.copyLayoutIdValue;
    if (!id) {
      return;
    }

    try {
      const response: LayoutDetailResponse = await this.api.query.getLayout(id);
      const baseName = response.name.trim().replace(/\s+/g, '_');
      const suffix = '_Copy';
      const maxBase = Math.max(0, layoutNameMaxLength - suffix.length);
      const truncatedBase = baseName.slice(0, maxBase);
      this.form.patchValue({
        name: truncatedBase.length > 0 ? `${truncatedBase}${suffix}` : 'Copy',
        description: response.description ?? '',
        format: this.normalizeFormat(response.format),
        isPrivate: !response.isPublic,
      });
      this.selectedFields = [...response.selectedFields];
    } catch {
      this.error = 'Failed to load layout details for copying.';
    }
  }

  private uncheckAllNodes(nodes: TreeNode[]): void {
    for (const node of nodes) {
      node.checked = false;
      this.uncheckAllNodes(node.children);
    }
  }

  async searchByName(): Promise<void> {
    const searchTerm = this.searchControl.value?.trim();
    if (!searchTerm) {
      this.searchResults = [];
      this.hasSearched = false;
      return;
    }

    this.searchLoading = true;
    this.selectedSearchResults.clear();
    try {
      const response = await this.api.query.getAvailableFields(searchTerm);
      this.searchResults = response.fields.sort((a, b) =>
        a.fieldName.localeCompare(b.fieldName),
      );
      this.hasSearched = true;
    } finally {
      this.searchLoading = false;
    }
  }

  /** Fields returned by search that are not already in the selected list. */
  get availableSearchResultCount(): number {
    return this.searchResults.filter((f) => !this.isFieldSelected(f.fieldId))
      .length;
  }

  isSearchResultSelected(field: LayoutFieldItem): boolean {
    return this.selectedSearchResults.has(field.fieldId);
  }

  toggleSearchResultSelection(field: LayoutFieldItem, event: MouseEvent): void {
    if (event.ctrlKey || event.metaKey) {
      if (this.selectedSearchResults.has(field.fieldId)) {
        this.selectedSearchResults.delete(field.fieldId);
      } else {
        this.selectedSearchResults.add(field.fieldId);
      }
    } else {
      this.selectedSearchResults.clear();
      this.selectedSearchResults.add(field.fieldId);
    }
  }

  addCheckedFields(): void {
    if (this.searchModeControl.value === 'Category') {
      const checkedNodes = this.getCheckedNodes(this.treeNodes);
      for (const node of checkedNodes) {
        if (!this.selectedFields.some((f) => f.fieldId === node.fieldId)) {
          this.selectedFields.push({
            fieldId: node.fieldId,
            fieldName: node.fieldName,
            category: '',
            description: node.description ?? '',
            displayOrder: this.selectedFields.length,
          });
        }
        node.checked = false;
      }
    } else {
      this.addFieldsByIds(Array.from(this.selectedSearchResults));
      this.selectedSearchResults.clear();
    }
    if (this.selectedFields.length > 0) {
      this.showSelectedFieldsError = false;
    }
  }

  private getCheckedNodes(nodes: TreeNode[]): TreeNode[] {
    const result: TreeNode[] = [];
    for (const node of nodes) {
      if (node.checked && !node.hasChildren) {
        result.push(node);
      }
      result.push(...this.getCheckedNodes(node.children));
    }
    return result;
  }

  removeSelectedFields(select: HTMLSelectElement): void {
    const selectedIds = new Set(this.getSelectedFieldIds(select));
    this.selectedFields = this.selectedFields.filter(
      (f) => !selectedIds.has(f.fieldId),
    );
    this.selectedFieldFocusIds = [];
    select.selectedIndex = -1;
    if (this.selectedFields.length > 0) {
      this.showSelectedFieldsError = false;
    }
  }

  onSelectedFieldsSelectionChange(select: HTMLSelectElement): void {
    this.selectedFieldFocusIds = this.getSelectedFieldIds(select);
  }

  moveSelectedFieldUp(select: HTMLSelectElement): void {
    const ids = this.getSelectedFieldIdsInListOrder(select);
    if (ids.length === 0) {
      this.snackbar.show('No Field Selected to Move', 'Info', null, 6000);
      return;
    }

    let moved = false;
    const next = [...this.selectedFields];
    for (const id of ids) {
      const index = next.findIndex((f) => f.fieldId === id);
      if (index <= 0) {
        continue;
      }
      const [field] = next.splice(index, 1);
      next.splice(index - 1, 0, field);
      moved = true;
    }

    if (!moved) {
      return;
    }

    this.selectedFields = next;
    this.selectedFieldFocusIds = ids;
    this.queueRestoreSelectedFields(select, ids);
  }

  moveSelectedFieldDown(select: HTMLSelectElement): void {
    const ids = this.getSelectedFieldIdsInListOrder(select);
    if (ids.length === 0) {
      this.snackbar.show('No Field Selected to Move', 'Info', null, 6000);
      return;
    }

    let moved = false;
    const next = [...this.selectedFields];
    for (const id of [...ids].reverse()) {
      const index = next.findIndex((f) => f.fieldId === id);
      if (index < 0 || index >= next.length - 1) {
        continue;
      }
      const [field] = next.splice(index, 1);
      next.splice(index + 1, 0, field);
      moved = true;
    }

    if (!moved) {
      return;
    }

    this.selectedFields = next;
    this.selectedFieldFocusIds = ids;
    this.queueRestoreSelectedFields(select, ids);
  }

  private getSelectedFieldIds(select: HTMLSelectElement): number[] {
    if (select.selectedOptions.length > 0) {
      return Array.from(select.selectedOptions)
        .map((option) => Number(option.value))
        .filter((id) => !Number.isNaN(id));
    }
    return [...this.selectedFieldFocusIds];
  }

  /** Legacy CreateLayout.aspx: Move Up/Down uses SelectedFields list order, not click order. */
  private getSelectedFieldIdsInListOrder(select: HTMLSelectElement): number[] {
    const selected = new Set(this.getSelectedFieldIds(select));
    if (selected.size === 0) {
      return [];
    }
    return this.selectedFields
      .filter((field) => selected.has(field.fieldId))
      .map((field) => field.fieldId);
  }

  private queueRestoreSelectedFields(
    select: HTMLSelectElement,
    fieldIds: number[],
  ): void {
    const selected = new Set(fieldIds);
    queueMicrotask(() => {
      for (const option of Array.from(select.options)) {
        option.selected = selected.has(Number(option.value));
      }
    });
  }

  private addFieldsByIds(ids: number[]): void {
    const searchFieldsMap = new Map(
      this.searchResults.map((f) => [f.fieldId, f]),
    );

    for (const id of ids) {
      if (!this.selectedFields.some((f) => f.fieldId === id)) {
        const field = searchFieldsMap.get(id);
        if (field) {
          this.selectedFields.push({
            ...field,
            displayOrder: this.selectedFields.length,
          });
        }
      }
    }
    if (this.selectedFields.length > 0) {
      this.showSelectedFieldsError = false;
    }
  }

  async save(): Promise<void> {
    this.error = '';
    this.success = '';

    this.form.markAllAsTouched();

    if (!this.form.valid) {
      const msg =
        this.firstFormErrorMessage() ??
        'Please correct the highlighted fields.';
      this.snackbar.show(msg, 'Error', null, 6000);
      return;
    }

    if (this.selectedFields.length === 0) {
      this.showSelectedFieldsError = true;
      this.snackbar.show(
        'You must select at least one field before saving',
        'Error',
        null,
        6000,
      );
      return;
    }

    this.showSelectedFieldsError = false;

    this.saving = true;
    try {
      const request: SaveLayoutRequest = {
        name: this.form.controls.name.value.trim(),
        description: this.form.controls.description.value.trim(),
        fieldIds: this.selectedFields.map((f) => f.fieldId),
        format: this.form.controls.format.value.trim().toUpperCase(),
        isPublic: !this.form.controls.isPrivate.value,
      };

      const id = this.layoutIdValue;
      const response = id
        ? await this.api.query.updateLayout(id, request)
        : await this.api.query.createLayout(request);

      if (!response.success) {
        const msg =
          response.message ||
          this.joinLayoutSaveErrors(response.errors) ||
          'Save failed.';
        this.snackbar.show(msg, 'Error', null, 6000);
        return;
      }

      if (id) {
        await this.loadLayout();
      }

      const savedLayoutId = id ?? response.layoutId;
      if (savedLayoutId == null) {
        this.snackbar.show(
          'Layout saved but could not determine layout id.',
          'Error',
          null,
          6000,
        );
        return;
      }

      this.success = 'Layout saved successfully.';
      this.saved.emit(savedLayoutId);
    } catch (e: unknown) {
      this.snackbar.show(this.httpSaveErrorMessage(e), 'Error', null, 6000);
    } finally {
      this.saving = false;
    }
  }

  private firstFormErrorMessage(): string | null {
    const keys = ['name', 'description', 'format'] as const;
    for (const key of keys) {
      const c = this.form.controls[key];
      const str = firstStringControlError(c);
      if (str) {
        return str;
      }
    }
    for (const key of keys) {
      const c = this.form.controls[key];
      if (c.invalid && c.errors?.['required']) {
        if (key === 'name') {
          return 'Layout Name cannot be blank';
        }
        if (key === 'description') {
          return 'Layout Description cannot be blank';
        }
        if (key === 'format') {
          return 'Format is required.';
        }
      }
    }
    return null;
  }

  private joinLayoutSaveErrors(
    errors: { message?: string }[] | undefined,
  ): string | null {
    if (!errors?.length) {
      return null;
    }
    const parts = errors
      .map((e) => e.message)
      .filter((m): m is string => !!m?.trim());
    return parts.length ? parts.join(' ') : null;
  }

  private httpSaveErrorMessage(e: unknown): string {
    if (e instanceof HttpErrorResponse) {
      const body = e.error;
      if (
        body &&
        typeof body === 'object' &&
        'message' in body &&
        typeof (body as { message?: unknown }).message === 'string'
      ) {
        const m = (body as { message: string }).message.trim();
        if (m.length > 0) {
          return m;
        }
      }
      return e.message || `Request failed (${e.status})`;
    }
    return 'Save failed.';
  }

  cancel(): void {
    this.cancelled.emit();
  }
}
